import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArchiveRestore, Eye, FileImage, FileText, Presentation, Share2, Star, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/finance";
import { fmtSize, shareOnePage } from "@/lib/onePages";
import { Section } from "@/components/common/DetailFields";
import { OnePageViewer } from "@/components/conecta/OnePageViewer";
import { OnePageUpload } from "@/components/conecta/OnePageUpload";

const icon = (pg) => (pg.kind === "pdf" ? FileText : pg.kind === "image" ? FileImage : Presentation);
const Mini = ({ onClick, children, testid, tone = "" }) => (
  <button data-testid={testid} onClick={onClick} className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg c-card flex items-center gap-1 ${tone}`}>{children}</button>
);

export const OnePageRow = ({ pg, onView, actions, showOpp }) => {
  const Icon = icon(pg);
  return (
    <li data-testid="onepage-item" className={`py-3 border-b c-line last:border-0 space-y-2 ${pg.is_principal ? "" : ""}`}>
      <button onClick={() => onView(pg)} className="w-full flex items-start gap-3 text-left">
        <Icon className={`w-8 h-8 shrink-0 ${pg.is_principal ? "c-accent" : "c-muted"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p data-testid="onepage-item-name" className="text-sm font-semibold truncate">{pg.original_filename}</p>
            {pg.is_principal && <span data-testid="onepage-principal-badge" className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#bc9538] text-[#0b2a47]">Principal</span>}
          </div>
          <p className="text-xs c-text-2">{pg.sender_name ? `De ${pg.sender_name}` : "Remitente sin especificar"} · {fmtDate(pg.received_date)} · {fmtSize(pg.size)}</p>
          {pg.note && <p className="text-xs c-muted">{pg.note}</p>}
          {showOpp && pg.opportunity_title && <Link to={`/conecta/negocios/${pg.opportunity_id}`} onClick={(e) => e.stopPropagation()} className="text-xs c-accent font-semibold hover:underline">{pg.opportunity_title}</Link>}
          <p className="text-[11px] c-muted">Subido por {pg.uploaded_by_name || "—"}{pg.is_archived ? ` · archivado por ${pg.archived_by_name || "—"}` : ""}</p>
        </div>
      </button>
      {actions && <div className="flex flex-wrap gap-1.5 pl-11">{actions}</div>}
    </li>
  );
};

const groupBySender = (items) => items.reduce((acc, pg) => {
  const k = pg.sender_name || "Sin especificar";
  (acc[k] = acc[k] || []).push(pg);
  return acc;
}, {});

const ArchivedList = ({ items, onView, onRestore }) => (
  <ul data-testid="onepage-archived-list" className="mt-1 opacity-80">
    {items.map((pg) => <OnePageRow key={pg.id} pg={pg} onView={onView} actions={<>
      <Mini testid="onepage-view-btn" onClick={() => onView(pg)}><Eye className="w-3 h-3" /> Ver</Mini>
      <Mini testid="onepage-restore-btn" onClick={() => onRestore(pg)}><ArchiveRestore className="w-3 h-3" /> Restaurar</Mini>
    </>} />)}
  </ul>
);

export const OnePagesSection = ({ oppId, onChanged }) => {
  const [data, setData] = useState(null);
  const [archived, setArchived] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api.get(`/opportunities/${oppId}/one-pages`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [oppId]);
  useEffect(() => { load(); }, [load]);
  const apply = (d, msg) => { setData(d); setArchived(null); toast.success(msg); onChanged?.(); };
  const act = async (pg, action, msg) => {
    try {
      const { data: d } = await api.post(`/one-pages/${pg.id}/${action}`);
      if (action === "restore") { setArchived(d.items); load(); toast.success(msg); onChanged?.(); } else apply(d, msg);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const share = async (pg) => { try { if ((await shareOnePage(pg)) === "downloaded") toast.success("Descargado"); } catch (e) { toast.error(errMsg(e)); } };
  const toggleArchived = async () => {
    if (archived) return setArchived(null);
    try { setArchived((await api.get(`/opportunities/${oppId}/one-pages`, { params: { archived: true } })).data.items); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return null;
  const principal = data.items.find((p) => p.is_principal);
  const rest = groupBySender(data.items.filter((p) => !p.is_principal));
  const actionsFor = (pg) => (
    <>
      <Mini testid="onepage-view-btn" onClick={() => setViewing(pg)}><Eye className="w-3 h-3" /> Ver</Mini>
      <Mini testid="onepage-share-btn" onClick={() => share(pg)}><Share2 className="w-3 h-3" /> Compartir</Mini>
      {!pg.is_principal && <Mini testid="onepage-principal-btn" onClick={() => act(pg, "principal", "Marcado como principal")}><Star className="w-3 h-3" /> Principal</Mini>}
      <Mini testid="onepage-archive-btn" tone="c-muted" onClick={() => act(pg, "archive", "Archivado")}><Archive className="w-3 h-3" /> Archivar</Mini>
    </>
  );
  return (
    <Section title="One Page" testid="onepage-section"
      action={<button data-testid="onepage-upload-btn" onClick={() => setUploading(true)} className="text-xs font-semibold px-3 py-1.5 rounded-lg c-btn-primary flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Subir one page</button>}>
      {data.items.length === 0 && <p data-testid="onepage-empty" className="text-sm c-muted">Aún no hay one page. Cuando alguien del negocio envíe su presentación, súbela aquí.</p>}
      <ul>
        {principal && <OnePageRow pg={principal} onView={setViewing} actions={actionsFor(principal)} />}
        {Object.entries(rest).map(([sender, items]) => (
          <li key={sender} className="pt-2">
            <p className="text-[11px] uppercase tracking-wider c-muted">{sender}</p>
            <ul>{items.map((pg) => <OnePageRow key={pg.id} pg={pg} onView={setViewing} actions={actionsFor(pg)} />)}</ul>
          </li>
        ))}
      </ul>
      {data.archived_count > 0 && (
        <button data-testid="onepage-archived-toggle" onClick={toggleArchived} className="mt-2 text-xs c-accent font-semibold">{archived ? "Ocultar archivados" : `Archivados (${data.archived_count})`}</button>
      )}
      {archived && <ArchivedList items={archived} onView={setViewing} onRestore={(pg) => act(pg, "restore", "Restaurado")} />}
      <OnePageViewer pg={viewing} open={!!viewing} onOpenChange={(v) => !v && setViewing(null)} />
      <OnePageUpload oppId={oppId} options={data.sender_options} open={uploading} onOpenChange={setUploading} onDone={(d) => apply(d, "Listo")} />
    </Section>
  );
};

export const ContactOnePages = ({ contactId }) => {
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);
  useEffect(() => { api.get(`/contacts/${contactId}/one-pages`).then((r) => setItems(r.data)).catch((e) => console.error("ContactOnePages: no se pudieron cargar", e?.response?.status ?? e?.name)); }, [contactId]);
  if (!items.length) return null;
  return (
    <Section title={`One pages enviados (${items.length})`} testid="contact-onepages">
      <ul>{items.map((pg) => <OnePageRow key={pg.id} pg={pg} onView={setViewing} showOpp />)}</ul>
      <OnePageViewer pg={viewing} open={!!viewing} onOpenChange={(v) => !v && setViewing(null)} />
    </Section>
  );
};
